// États et libellés du lot 3 (cadrage, sections 4.2 à 4.4 ; D-020) : besoins, ordres de transport, étapes,
// réceptions, rapprochements. Les transitions permises sont codées ici, une seule fois, pour le serveur comme
// pour l'interface. Aucun état ni libellé ne qualifie une fraude (cadrage 4.4).

export const ETATS_BESOIN = ['brouillon', 'soumis', 'accepte', 'planifie', 'livre', 'partiellement_livre', 'refuse', 'annule'] as const;
export type EtatBesoin = (typeof ETATS_BESOIN)[number];

export const LIBELLES_ETATS_BESOIN: Record<EtatBesoin, string> = {
  brouillon: 'Brouillon',
  soumis: 'Soumis',
  accepte: 'Accepté',
  planifie: 'Planifié',
  livre: 'Livré',
  partiellement_livre: 'Partiellement livré',
  refuse: 'Refusé',
  annule: 'Annulé',
};

export const URGENCES_BESOIN = ['normale', 'urgente', 'prioritaire'] as const;
export type UrgenceBesoin = (typeof URGENCES_BESOIN)[number];

export const LIBELLES_URGENCES: Record<UrgenceBesoin, string> = {
  normale: 'Normale',
  urgente: 'Urgente',
  prioritaire: 'Prioritaire (établissement stratégique)',
};

const TRANSITIONS_BESOIN: Record<EtatBesoin, readonly EtatBesoin[]> = {
  brouillon: ['soumis', 'annule'],
  soumis: ['accepte', 'refuse', 'annule'],
  accepte: ['planifie', 'annule'],
  planifie: ['livre', 'partiellement_livre', 'accepte', 'annule'],
  partiellement_livre: ['livre', 'planifie'],
  livre: [],
  refuse: [],
  annule: [],
};

export function transitionBesoinPermise(de: EtatBesoin, vers: EtatBesoin): boolean {
  return TRANSITIONS_BESOIN[de].includes(vers);
}

export const ETATS_COMMANDE = ['ouverte', 'planifiee', 'cloturee', 'annulee'] as const;
export type EtatCommande = (typeof ETATS_COMMANDE)[number];
export const LIBELLES_ETATS_COMMANDE: Record<EtatCommande, string> = {
  ouverte: 'Ouverte',
  planifiee: 'Planifiée',
  cloturee: 'Clôturée',
  annulee: 'Annulée',
};

export const ETATS_ORDRE = ['emis', 'charge', 'en_route', 'arrive', 'recu_partiellement', 'recu', 'solde', 'en_rapprochement', 'annule'] as const;
export type EtatOrdre = (typeof ETATS_ORDRE)[number];

export const LIBELLES_ETATS_ORDRE: Record<EtatOrdre, string> = {
  emis: 'Émis',
  charge: 'Chargé',
  en_route: 'En route',
  arrive: 'Arrivé',
  recu_partiellement: 'Reçu partiellement',
  recu: 'Reçu',
  solde: 'Soldé',
  en_rapprochement: 'En rapprochement',
  annule: 'Annulé',
};

const TRANSITIONS_ORDRE: Record<EtatOrdre, readonly EtatOrdre[]> = {
  emis: ['charge', 'annule'],
  charge: ['en_route', 'arrive', 'annule'],
  en_route: ['arrive', 'annule'],
  arrive: ['recu', 'recu_partiellement', 'en_rapprochement', 'annule'],
  recu_partiellement: ['solde', 'en_rapprochement'],
  // Une réception reçue ou soldée peut encore être contestée par une partie (R-16) : rapprochement.
  recu: ['solde', 'en_rapprochement'],
  en_rapprochement: ['solde'],
  solde: ['en_rapprochement'],
  annule: [],
};

export function transitionOrdrePermise(de: EtatOrdre, vers: EtatOrdre): boolean {
  return TRANSITIONS_ORDRE[de].includes(vers);
}

/** Ordres dont le produit est physiquement en transport (chargé, non encore reçu en totalité). */
export const ETATS_ORDRE_EN_COURS: readonly EtatOrdre[] = ['charge', 'en_route', 'arrive'];
/** Ordres dont la mission n'est pas terminée (y compris émis, en attente de chargement). */
export const ETATS_ORDRE_ACTIFS: readonly EtatOrdre[] = ['emis', 'charge', 'en_route', 'arrive', 'recu_partiellement'];
/** Ordres qui portent une réception (totale ou partielle). */
export const ETATS_ORDRE_RECUS: readonly EtatOrdre[] = ['recu_partiellement', 'recu', 'solde', 'en_rapprochement'];

/** Un ordre annulé après chargement donne lieu à un mouvement de retour au dépôt, jamais à une suppression (R-20). */
export function annulationExigeRetour(etat: EtatOrdre): boolean {
  return etat === 'charge' || etat === 'en_route' || etat === 'arrive';
}

export const TYPES_ETAPE = ['depart', 'etape', 'arret', 'incident', 'arrivee'] as const;
export type TypeEtape = (typeof TYPES_ETAPE)[number];

export const LIBELLES_TYPES_ETAPE: Record<TypeEtape, string> = {
  depart: 'Départ',
  etape: 'Étape',
  arret: 'Arrêt',
  incident: 'Incident',
  arrivee: 'Arrivée',
};

/** Étapes acceptées selon l'état de l'ordre, et l'état qui en résulte. */
export function effetEtape(etat: EtatOrdre, type: TypeEtape): { permise: boolean; etatSuivant: EtatOrdre; motif: string | null } {
  if (type === 'depart') {
    if (etat === 'charge') return { permise: true, etatSuivant: 'en_route', motif: null };
    return { permise: false, etatSuivant: etat, motif: etat === 'emis' ? 'Le départ suit la confirmation du chargement par le dépôt' : 'Départ déjà enregistré' };
  }
  if (type === 'arrivee') {
    if (etat === 'en_route' || etat === 'charge') return { permise: true, etatSuivant: 'arrive', motif: null };
    return { permise: false, etatSuivant: etat, motif: etat === 'emis' ? 'Arrivée impossible avant le chargement' : 'Arrivée déjà enregistrée' };
  }
  if (etat === 'charge' || etat === 'en_route' || etat === 'arrive') return { permise: true, etatSuivant: etat, motif: null };
  return { permise: false, etatSuivant: etat, motif: 'Étape possible uniquement pendant la mission (après chargement, avant réception)' };
}

export const SOURCES_POSITION = ['declaree', 'appareil'] as const;
export type SourcePosition = (typeof SOURCES_POSITION)[number];
export const LIBELLES_SOURCES_POSITION: Record<SourcePosition, string> = {
  declaree: 'Position déclarée par le conducteur',
  appareil: 'Position de l’appareil',
};

export const ETATS_RECEPTION = ['en_attente_validation', 'validee', 'rejetee'] as const;
export type EtatReception = (typeof ETATS_RECEPTION)[number];
export const LIBELLES_ETATS_RECEPTION: Record<EtatReception, string> = {
  en_attente_validation: 'En attente de validation',
  validee: 'Validée',
  rejetee: 'Rejetée',
};

export const ETATS_RAPPROCHEMENT = ['ouvert', 'en_instruction', 'clos'] as const;
export type EtatRapprochement = (typeof ETATS_RAPPROCHEMENT)[number];
export const LIBELLES_ETATS_RAPPROCHEMENT: Record<EtatRapprochement, string> = {
  ouvert: 'Ouvert',
  en_instruction: 'En instruction',
  clos: 'Clos',
};

export const ORIGINES_RAPPROCHEMENT = ['livraison', 'reliquat_expire', 'inventaire', 'document', 'contestation'] as const;
export type OrigineRapprochement = (typeof ORIGINES_RAPPROCHEMENT)[number];
export const LIBELLES_ORIGINES_RAPPROCHEMENT: Record<OrigineRapprochement, string> = {
  livraison: 'Livraison (écart au-delà de la tolérance)',
  reliquat_expire: 'Reliquat d’une réception partielle non reçu dans le délai',
  inventaire: 'Inventaire',
  document: 'Document',
  contestation: 'Contestation d’une réception',
};

/** Hypothèses d'origine d'un écart (module G1). Formulations techniques, jamais une qualification de fraude. */
export const HYPOTHESES_ECART = [
  'mesure_chargement',
  'mesure_reception',
  'dilatation_thermique',
  'compartiment_non_vide',
  'perte_en_route',
  'etalonnage_instrument',
  'erreur_saisie',
  'autre',
] as const;
export type HypotheseEcart = (typeof HYPOTHESES_ECART)[number];
export const LIBELLES_HYPOTHESES_ECART: Record<HypotheseEcart, string> = {
  mesure_chargement: 'Mesure au chargement',
  mesure_reception: 'Mesure à la réception',
  dilatation_thermique: 'Variation de température (dilatation)',
  compartiment_non_vide: 'Compartiment non entièrement vidé',
  perte_en_route: 'Perte ou fuite en route',
  etalonnage_instrument: 'Étalonnage d’un instrument',
  erreur_saisie: 'Erreur de saisie',
  autre: 'Autre hypothèse, précisée dans l’observation',
};

/** Décisions de clôture (cadrage 4.3) : correction, justification technique, signalement aux services compétents. */
export const DECISIONS_CLOTURE = ['correction', 'justification_technique', 'signalement_services_competents'] as const;
export type DecisionCloture = (typeof DECISIONS_CLOTURE)[number];
export const LIBELLES_DECISIONS_CLOTURE: Record<DecisionCloture, string> = {
  correction: 'Correction',
  justification_technique: 'Justification technique',
  signalement_services_competents: 'Signalement aux services compétents',
};

export const TYPES_INCIDENT_STATION = ['pompe', 'rupture', 'fermeture', 'securite', 'electricite', 'qualite_produit', 'autre'] as const;
export type TypeIncidentStation = (typeof TYPES_INCIDENT_STATION)[number];
export const LIBELLES_TYPES_INCIDENT_STATION: Record<TypeIncidentStation, string> = {
  pompe: 'Pompe ou distributeur en panne',
  rupture: 'Rupture de produit',
  fermeture: 'Fermeture imprévue',
  securite: 'Sécurité des personnes ou des biens',
  electricite: 'Coupure d’électricité',
  qualite_produit: 'Doute sur la qualité du produit',
  autre: 'Autre',
};

export const ETATS_INCIDENT_STATION = ['signale', 'en_traitement', 'clos'] as const;
export type EtatIncidentStation = (typeof ETATS_INCIDENT_STATION)[number];
export const LIBELLES_ETATS_INCIDENT_STATION: Record<EtatIncidentStation, string> = {
  signale: 'Signalé',
  en_traitement: 'En traitement',
  clos: 'Clos',
};

export const SOURCES_VENTES = ['saisie', 'import', 'api', 'hors_ligne'] as const;
export type SourceVentes = (typeof SOURCES_VENTES)[number];

export const JOURS_SEMAINE = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const;
export type JourSemaine = (typeof JOURS_SEMAINE)[number];
